/**
 * Account: profile, Access Pass status and purchase, redeem codes, referrals,
 * preferences (language, theme) and help. Works for guests too (sign-in prompt + preferences).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { IconTile, Page, PageHeader, SectionCard } from '../ui';
import { useAuth } from '../../utils/authUtils';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useLanguage } from '../../utils/LanguageContext';
import { useColorMode } from '../../theme';
import { ACTION_LABELS } from '../../constants/gateConfig';
import { updateProfile, redeemCode as redeemCodeApi, getReferralCode, getReferralStats } from '../../utils/api';
import { formatDate } from '../../utils/format';
import { getPlanBenefits } from './planBenefits';

const APP_VERSION = '2.0.0';
const RECOMMENDED_PASS = 'planning_pass';
const USAGE_ACTIONS = ['quick_estimate', 'complete_home_estimate', 'material_estimate'];

const text = {
  en: {
    title: 'Account',
    guestTitle: 'Sign in to get more',
    guestDesc: 'Save projects, get more free estimates every day, and buy Access Passes.',
    signIn: 'Sign in',
    createAccount: 'Create free account',
    memberSince: 'Member since',
    phone: 'Phone',
    addPhone: 'Add phone number',
    editPhone: 'Edit',
    signOut: 'Sign out',
    yourPass: 'Your plan',
    free: 'Free plan',
    freeDesc: 'Limited free estimates every day.',
    active: 'Active',
    expired: 'Expired',
    expiredDesc: 'Your pass has expired. Get another pass to unlock everything again.',
    timeLeft: 'Time left',
    todayUsage: "Today's free usage",
    unlimited: 'Unlimited',
    of: 'of',
    passes: 'Access Passes',
    passesDesc: 'Pay once for full access for a few days — no subscription, nothing renews automatically.',
    paymentNote: 'Secure payment by FPX online banking or card via ToyyibPay.',
    recommended: 'Most popular',
    current: 'Current',
    buy: 'Get',
    signInToBuy: 'Sign in to buy',
    redeem: 'Have a code?',
    redeemPlaceholder: 'Enter redeem code',
    redeemBtn: 'Redeem',
    redeemSuccess: 'Code redeemed! Your pass is now active.',
    referTitle: 'Invite friends',
    referDesc: 'You and your friend each get a free 3-day Planning Pass when they sign up with your link.',
    copyLink: 'Copy link',
    copied: 'Copied!',
    share: 'WhatsApp',
    friends: 'Friends joined',
    passesEarned: 'Hours earned',
    preferences: 'Preferences',
    language: 'Language',
    darkMode: 'Dark mode',
    help: 'Help & about',
    phoneTitle: 'Phone number',
    phoneDesc: 'ToyyibPay needs a phone number to process your payment.',
    phoneLabel: 'Phone number',
    phoneInvalid: 'Enter a valid phone number',
    save: 'Save',
    cancel: 'Cancel',
    phoneSaved: 'Phone number saved',
    paymentFailed: 'Could not start the payment. Please try again.',
    version: 'Version',
    by: 'RumahKira™ by Kondingau Community Insight (KCi)',
    faq: [
      ['How accurate are the estimates?', 'They are planning estimates based on typical Malaysian construction rates and standard material ratios. Actual costs depend on your site, design, contractor and material prices — always get quotations before you build.'],
      ['Where are my projects saved?', 'Saved projects are stored in this browser on this device. Clearing your browser data removes them, and they do not appear on other devices.'],
      ['What does an Access Pass include?', 'A pass unlocks unlimited estimates and calculations for its duration. What each pass includes (saved projects, comparisons, PDF reports) is listed on its card above. Passes are one-time payments and never renew automatically.'],
      ['What is not included in the cost?', 'Land, earthworks, piling, fencing and landscaping, built-in furniture, professional fees and permit fees are not included.'],
    ],
  },
  my: {
    title: 'Akaun',
    guestTitle: 'Log masuk untuk lebih',
    guestDesc: 'Simpan projek, dapat lebih banyak anggaran percuma setiap hari, dan beli Pas Akses.',
    signIn: 'Log masuk',
    createAccount: 'Cipta akaun percuma',
    memberSince: 'Ahli sejak',
    phone: 'Telefon',
    addPhone: 'Tambah nombor telefon',
    editPhone: 'Ubah',
    signOut: 'Log keluar',
    yourPass: 'Pelan anda',
    free: 'Pelan percuma',
    freeDesc: 'Anggaran percuma yang terhad setiap hari.',
    active: 'Aktif',
    expired: 'Tamat',
    expiredDesc: 'Pas anda telah tamat. Dapatkan pas lain untuk membuka semua ciri semula.',
    timeLeft: 'Baki masa',
    todayUsage: 'Penggunaan percuma hari ini',
    unlimited: 'Tanpa had',
    of: 'daripada',
    passes: 'Pas Akses',
    passesDesc: 'Bayar sekali untuk akses penuh beberapa hari — tiada langganan, tiada pembaharuan automatik.',
    paymentNote: 'Pembayaran selamat melalui FPX perbankan dalam talian atau kad melalui ToyyibPay.',
    recommended: 'Paling popular',
    current: 'Semasa',
    buy: 'Dapatkan',
    signInToBuy: 'Log masuk untuk beli',
    redeem: 'Ada kod?',
    redeemPlaceholder: 'Masukkan kod tebus',
    redeemBtn: 'Tebus',
    redeemSuccess: 'Kod berjaya ditebus! Pas anda kini aktif.',
    referTitle: 'Jemput rakan',
    referDesc: 'Anda dan rakan masing-masing dapat Pas Perancangan 3 hari percuma apabila mereka daftar dengan pautan anda.',
    copyLink: 'Salin pautan',
    copied: 'Disalin!',
    share: 'WhatsApp',
    friends: 'Rakan menyertai',
    passesEarned: 'Jam diperoleh',
    preferences: 'Tetapan',
    language: 'Bahasa',
    darkMode: 'Mod gelap',
    help: 'Bantuan & tentang',
    phoneTitle: 'Nombor telefon',
    phoneDesc: 'ToyyibPay memerlukan nombor telefon untuk memproses pembayaran anda.',
    phoneLabel: 'Nombor telefon',
    phoneInvalid: 'Masukkan nombor telefon yang sah',
    save: 'Simpan',
    cancel: 'Batal',
    phoneSaved: 'Nombor telefon disimpan',
    paymentFailed: 'Tidak dapat memulakan pembayaran. Sila cuba lagi.',
    version: 'Versi',
    by: 'RumahKira™ oleh Kondingau Community Insight (KCi)',
    faq: [
      ['Sejauh mana ketepatan anggaran?', 'Ia anggaran perancangan berdasarkan kadar pembinaan biasa di Malaysia dan nisbah bahan standard. Kos sebenar bergantung pada tapak, reka bentuk, kontraktor dan harga bahan — sentiasa dapatkan sebut harga sebelum membina.'],
      ['Di mana projek saya disimpan?', 'Projek disimpan dalam pelayar ini pada peranti ini. Memadam data pelayar akan membuangnya, dan ia tidak muncul pada peranti lain.'],
      ['Apa yang termasuk dalam Pas Akses?', 'Pas membuka anggaran dan pengiraan tanpa had sepanjang tempohnya. Apa yang termasuk dalam setiap pas (simpan projek, perbandingan, laporan PDF) disenaraikan pada kadnya di atas. Pas ialah bayaran sekali dan tidak diperbaharui secara automatik.'],
      ['Apa yang tidak termasuk dalam kos?', 'Tanah, kerja tanah, cerucuk, pagar dan landskap, perabot terbina, fi profesional dan fi permit tidak termasuk.'],
    ],
  },
};

// ---------------------------------------------------------------------------

const PlanCard = ({ plan, t, language, isCurrent, loading, onBuy, canBuy }) => {
  const my = language === 'my';
  const recommended = !!plan.is_recommended || plan.plan_code === RECOMMENDED_PASS;
  const duration = my ? plan.duration_text_my : plan.duration_text_en;
  return (
    <Card
      sx={(theme) => ({
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        borderColor: recommended ? 'primary.main' : 'divider',
        borderWidth: recommended ? 1.5 : 1,
        bgcolor: recommended ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.025) : 'background.paper',
      })}
    >
      {recommended && (
        <Chip
          size="small"
          color="primary"
          label={t.recommended}
          sx={{ position: 'absolute', top: -12, left: 16, fontWeight: 700 }}
        />
      )}
      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="subtitle1">{my ? plan.name_my || plan.name_en : plan.name_en}</Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.5 }}>
          <Typography className="tabular" sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
            RM{Number(plan.price) % 1 === 0 ? Number(plan.price) : Number(plan.price).toFixed(2)}
          </Typography>
          {duration && (
            <Typography variant="body2" color="text.secondary">
              / {duration}
            </Typography>
          )}
        </Stack>
        <Stack spacing={0.75} sx={{ my: 2, flex: 1 }}>
          {getPlanBenefits(plan, language).map((b) => (
            <Stack key={b} direction="row" spacing={1} alignItems="flex-start">
              <CheckRoundedIcon color="success" sx={{ fontSize: 18, mt: '1px' }} />
              <Typography variant="body2">{b}</Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          fullWidth
          variant={recommended ? 'contained' : 'outlined'}
          disabled={isCurrent || loading}
          onClick={onBuy}
          startIcon={!canBuy ? <LockOutlinedIcon /> : undefined}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : isCurrent ? t.current : canBuy ? `${t.buy} ${my ? plan.name_my || plan.name_en : plan.name_en}` : t.signInToBuy}
        </Button>
      </CardContent>
    </Card>
  );
};

const UsageRow = ({ label, data, t }) => {
  if (!data) return null;
  const unlimited = data.limit === -1;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600} className="tabular">
          {unlimited ? t.unlimited : `${data.used} ${t.of} ${data.limit}`}
        </Typography>
      </Stack>
      {!unlimited && data.limit > 0 && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (data.used / data.limit) * 100)}
          color={data.remaining <= 0 ? 'error' : data.remaining <= 1 ? 'warning' : 'primary'}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------

const PreferencesCard = ({ t }) => {
  const { language, changeLanguage } = useLanguage();
  const { mode, toggleColorMode } = useColorMode();
  return (
    <SectionCard title={t.preferences}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="body2">{t.language}</Typography>
          <ToggleButtonGroup size="small" exclusive value={language} onChange={(e, v) => v && changeLanguage(v)}>
            <ToggleButton value="en" sx={{ px: 2 }}>English</ToggleButton>
            <ToggleButton value="my" sx={{ px: 2 }}>Bahasa Malaysia</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Divider />
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2">{t.darkMode}</Typography>
          <Switch checked={mode === 'dark'} onChange={toggleColorMode} inputProps={{ 'aria-label': t.darkMode }} />
        </Stack>
      </Stack>
    </SectionCard>
  );
};

const HelpCard = ({ t }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
      {t.help}
    </Typography>
    <Stack spacing={1}>
      {t.faq.map(([q, a]) => (
        <Accordion key={q}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Typography variant="body2" fontWeight={600}>
              {q}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {a}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
    <Typography variant="caption" color="text.disabled" component="p" sx={{ textAlign: 'center', mt: 3 }}>
      {t.by} · {t.version} {APP_VERSION}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------

const AccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = text[language] || text.en;
  const { user, loading: authLoading, refreshUser, signOut } = useAuth();
  const {
    accessPass,
    plans,
    actionLimits,
    initiatePayment,
    refreshSubscription,
    loading: subLoading,
    hasActivePass,
    isFreePlan,
    getPassName,
    getTimeRemainingText,
    timeRemaining,
  } = useSubscription();

  const passesRef = useRef(null);
  const [message, setMessage] = useState(null); // { severity, text }
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [phoneDialog, setPhoneDialog] = useState({ open: false, planCode: null });
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  // Referral link and stats
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    Promise.all([getReferralCode(), getReferralStats()])
      .then(([codeRes, statsRes]) => {
        if (cancelled) return;
        setReferral({
          link: codeRes.success ? codeRes.data.referral_link : null,
          shareText: codeRes.success ? { en: codeRes.data.share_text_en, my: codeRes.data.share_text_my } : {},
          stats: statsRes.success ? statsRes.data : null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Deep link to the passes section (e.g. from the upgrade prompt)
  useEffect(() => {
    if (location.hash === '#passes' && plans.length && passesRef.current) {
      passesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, plans.length, subLoading]);

  const startPayment = async (planCode) => {
    setPaymentLoading(planCode);
    setMessage(null);
    try {
      const response = await initiatePayment(planCode);
      if (response.success && response.data.payment_url) {
        window.location.href = response.data.payment_url;
        return;
      }
      setMessage({ severity: 'error', text: response.message || t.paymentFailed });
    } catch (err) {
      if (err.error_code === 'PHONE_REQUIRED') {
        setPhoneDialog({ open: true, planCode });
      } else {
        setMessage({ severity: 'error', text: err.message || t.paymentFailed });
      }
    }
    setPaymentLoading(null);
  };

  const handleBuy = (planCode) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!user.phone) {
      setPhoneDialog({ open: true, planCode });
      return;
    }
    startPayment(planCode);
  };

  const handleSavePhone = async () => {
    const phone = phoneInput.replace(/[^0-9+]/g, '');
    if (phone.length < 9) {
      setMessage({ severity: 'error', text: t.phoneInvalid });
      return;
    }
    setPhoneSaving(true);
    try {
      const response = await updateProfile({ phone });
      if (!response.success) throw new Error(response.message);
      await refreshUser();
      const { planCode } = phoneDialog;
      setPhoneDialog({ open: false, planCode: null });
      setPhoneInput('');
      setMessage({ severity: 'success', text: t.phoneSaved });
      // Continue straight to payment; don't re-check the (stale) user object
      if (planCode) startPayment(planCode);
    } catch (err) {
      setMessage({ severity: 'error', text: err.message || t.phoneInvalid });
    }
    setPhoneSaving(false);
  };

  const handleRedeem = async () => {
    const code = redeemInput.trim();
    if (!code) return;
    setRedeemLoading(true);
    setMessage(null);
    try {
      const response = await redeemCodeApi(code);
      if (!response.success) throw new Error(response.message);
      setRedeemInput('');
      setMessage({ severity: 'success', text: response.message || t.redeemSuccess });
      await Promise.all([refreshUser(), refreshSubscription()]);
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    }
    setRedeemLoading(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referral.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked; the link is visible for manual copy
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/app');
  };

  const paidPlans = (plans || []).filter((p) => p.plan_code !== 'free' && Number(p.price) > 0);
  const loading = authLoading || (user && subLoading);
  const labels = ACTION_LABELS[language] || ACTION_LABELS.en;

  return (
    <Page>
      <PageHeader title={t.title} />

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      ) : !user ? (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6">{t.guestTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {t.guestDesc}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" onClick={() => navigate('/auth?mode=signup')}>
                {t.createAccount}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/auth')}>
                {t.signIn}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Profile */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700, fontSize: '1.4rem' }}>
                  {(user.name || user.email || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" noWrap>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                  {user.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      {t.memberSince} {formatDate(user.createdAt, language)}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t.phone}
                  </Typography>
                  <Typography variant="body2">{user.phone || '—'}</Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => {
                    setPhoneInput(user.phone || '');
                    setPhoneDialog({ open: true, planCode: null });
                  }}
                >
                  {user.phone ? t.editPhone : t.addPhone}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Current plan */}
          <SectionCard title={t.yourPass} icon={<WorkspacePremiumRoundedIcon />}>
            {hasActivePass ? (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">{getPassName(language)}</Typography>
                  <Chip size="small" color="success" label={t.active} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.timeLeft}: <strong>{getTimeRemainingText(language)}</strong>
                </Typography>
                {accessPass?.duration_hours && timeRemaining?.total_seconds > 0 && (
                  <LinearProgress
                    variant="determinate"
                    color="success"
                    value={Math.min(100, (timeRemaining.total_seconds / (accessPass.duration_hours * 3600)) * 100)}
                    sx={{ mt: 1.5 }}
                  />
                )}
              </Box>
            ) : (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">{t.free}</Typography>
                  {!isFreePlan && <Chip size="small" label={t.expired} />}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {!isFreePlan ? t.expiredDesc : t.freeDesc}
                </Typography>
                {actionLimits && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                      {t.todayUsage}
                    </Typography>
                    <Stack spacing={1.5}>
                      {USAGE_ACTIONS.map((code) => (
                        <UsageRow key={code} label={labels[code]} data={actionLimits[code]} t={t} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </SectionCard>
        </>
      )}

      {/* Passes */}
      {paidPlans.length > 0 && (
        <Box ref={passesRef} id="passes" sx={{ mb: 2, scrollMarginTop: 80 }}>
          <Typography variant="h6" component="h2">
            {t.passes}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {t.passesDesc}
          </Typography>
          <Grid container spacing={2}>
            {paidPlans.map((plan) => (
              <Grid item xs={12} sm={4} key={plan.plan_code}>
                <PlanCard
                  plan={plan}
                  t={t}
                  language={language}
                  canBuy={!!user}
                  isCurrent={hasActivePass && accessPass?.type === plan.plan_code}
                  loading={paymentLoading === plan.plan_code}
                  onBuy={() => handleBuy(plan.plan_code)}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            {t.paymentNote}
          </Typography>
        </Box>
      )}

      {user && (
        <>
          {/* Redeem */}
          <SectionCard title={t.redeem}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder={t.redeemPlaceholder}
                value={redeemInput}
                onChange={(e) => setRedeemInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                inputProps={{ 'aria-label': t.redeemPlaceholder, style: { fontFamily: 'monospace', letterSpacing: 1 } }}
              />
              <Button variant="outlined" onClick={handleRedeem} disabled={!redeemInput.trim() || redeemLoading} sx={{ flexShrink: 0 }}>
                {redeemLoading ? <CircularProgress size={20} /> : t.redeemBtn}
              </Button>
            </Stack>
          </SectionCard>

          {/* Referral */}
          {referral?.link && (
            <SectionCard title={t.referTitle} icon={<CardGiftcardRoundedIcon />}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t.referDesc}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={referral.link}
                InputProps={{ readOnly: true }}
                inputProps={{ 'aria-label': t.referTitle }}
                onFocus={(e) => e.target.select()}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={handleCopy}>
                  {copied ? t.copied : t.copyLink}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  href={`https://wa.me/?text=${encodeURIComponent(referral.shareText[language] || referral.shareText.en || referral.link)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.share}
                </Button>
              </Stack>
              {referral.stats && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="h5" className="tabular">
                      {referral.stats.total_referrals ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.friends}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h5" className="tabular">
                      {referral.stats.total_hours_earned ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.passesEarned}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </SectionCard>
          )}
        </>
      )}

      <PreferencesCard t={t} />
      <HelpCard t={t} />

      {user && (
        <Button fullWidth color="error" variant="outlined" startIcon={<LogoutRoundedIcon />} onClick={handleSignOut} sx={{ mt: 1 }}>
          {t.signOut}
        </Button>
      )}

      <Dialog open={phoneDialog.open} onClose={() => setPhoneDialog({ open: false, planCode: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{t.phoneTitle}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <IconTile icon={<WorkspacePremiumRoundedIcon />} size={36} />
            <Typography variant="body2" color="text.secondary">
              {t.phoneDesc}
            </Typography>
          </Stack>
          <TextField
            autoFocus
            fullWidth
            label={t.phoneLabel}
            placeholder="0123456789"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            inputProps={{ inputMode: 'tel' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPhoneDialog({ open: false, planCode: null })}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSavePhone} disabled={phoneSaving}>
            {phoneSaving ? <CircularProgress size={20} color="inherit" /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default AccountPage;
